import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useAlert } from 'src/hooks/useAlert';
import { useSubmit } from 'src/hooks/useSubmit';
import api from 'src/services/api';

import { getCardValues } from '../utils/getCardValues';
import PagamentoService from '../services/Pagamento';

import { CVC_PATTERN, DATA_VENCIMENTO_CARTAO } from 'src/consts/patterns';

import { Container } from 'src/components/Layouts/Container';
import { Centered } from 'src/components/Layouts/Container/styles';
import { Input, InputMask } from 'src/components/Form';
import { CardInput } from 'src/modules/pagamento/components/CardInput';
import FlagCards from 'src/modules/pagamento/components/FlagCards';

import Header from './Header';
import AberturaEmpresaInfo from './AberturaEmpresaInfo';
import ChooseDefaultCardShow from './ChooseDefaultCardShow';

import schema from './schema';

import { InputButton } from './styles';

export function PagamentoComCartao() {
  const location = useLocation();
  const history = useHistory();

  const { type, isOpeningCompany, plano } = location.state;

  const usuario = useSelector((state) => state.user);

  const { alert, openAlert, closeAlert } = useAlert();

  const [showCardForm, setShowCardForm] = useState(false);
  const [formSubmiting, setFormSubmiting] = useState(false);
  const [cupom, setCupom] = useState('');
  const [hasDefaultCard, setHasDefaultCard] = useState(false);
  const [loadingCardData, setLoadingCardData] = useState(true);
  const [idPagamento, setIdPagamento] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const [submitCupom, cupomValidado, isCupomSubmiting] = useSubmit(async () => {
    return api.post('/validarCupom', {
      tipo_utilizacao: type,
      cupom,
      id_usuario: usuario.id,
    });
  });

  const handleAssinaturaPagamento = async (formData) => {
    try {
      setFormSubmiting(true);

      const { email } = usuario;

      if (hasDefaultCard) {
        await PagamentoService.contratarAssinatura({
          email,
          id_payment: idPagamento,
          identificador_iugu: plano.identificador_iugu,
          cupom: cupomValidado,
        });
      } else {
        const tratedCard = getCardValues(formData);

        await PagamentoService.contratarAssinatura({
          ...tratedCard,
          email,
          identificador_iugu: plano.identificador_iugu,
          cupom: cupomValidado,
        });
      }

      history.replace({
        pathname: '/usuarioAssinatura',
        state: {
          hasContratarAssinaturaSuccess: true,
        },
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      openAlert(
        errorMessage ?? 'Falha ao tentar realizar contratação!',
        'error'
      );
    } finally {
      setFormSubmiting(false);
    }
  };

  const handleServicoPagamento = async (formData) => {
    try {
      setFormSubmiting(true);

      const { id, email } = usuario;

      const item = {
        description: plano.nome,
        quantity: 1,
        price_cents: (parseFloat(plano.valor) * 100).toFixed(0),
      };

      if (hasDefaultCard) {
        await PagamentoService.contratarServicoComCartaoCredito({
          id,
          email,
          id_payment: idPagamento,
          servicoId: plano.id,
          item,
          identificador_iugu: plano.identificador_iugu,
          cupom: cupomValidado,
        });
      } else {
        const tratedCard = getCardValues(formData);

        await PagamentoService.contratarServicoComCartaoCredito({
          ...tratedCard,
          id,
          email,
          servicoId: plano.id,
          item,
          identificador_iugu: plano.identificador_iugu,
          cupom: cupomValidado,
        });
      }

      if (
        plano?.uuid === 'd490cb59-db44-4a6b-87b9-07a285d79236' ||
        plano?.uuid === '0f36236d-e234-4656-a6e8-6ea2ac5dcf8d'
      ) {
        history.replace({
          pathname: '/usuarioEmpresa',
          state: {
            message:
              'Serviço contratado, preencha os dados para a abertura da sua empresa!',
          },
        });
      }

      history.replace({
        pathname: `/servicosUsuario/detalhe/${plano.id}`,
        state: {
          message: 'Serviço contratado com sucesso.',
          servico: plano,
        },
      });
    } catch (error) {
      openAlert(
        error?.response?.data?.message ??
          'Falha ao tentar realizar contratação!',
        'error'
      );
    } finally {
      setFormSubmiting(false);
    }
  };

  const handleContratarSolicitacaoPress = () => {
    if (type === 'ASSINATURA') {
      if (hasDefaultCard) {
        handleAssinaturaPagamento();
      } else {
        handleSubmit(handleAssinaturaPagamento)();
      }
    } else {
      if (hasDefaultCard) {
        handleServicoPagamento();
      } else {
        handleSubmit(handleServicoPagamento)();
      }
    }
  };

  const handleCardChooseFinish = (data) => {
    setShowCardForm(true);
    setLoadingCardData(false);

    if (data) {
      if (data?.month && data?.month) {
        const { month, year } = data;

        setHasDefaultCard(true);

        setIdPagamento(data.idPagamento);

        reset({
          ...data,
          expiration_date: `${month < 10 ? `0${month}` : month}/${year}`,
        });
      }
    }
  };

  return (
    <Container>
      <Collapse in={alert.open}>
        <Alert severity={alert.severity} onClick={closeAlert}>
          {alert.message}
        </Alert>
      </Collapse>
      {isOpeningCompany && (
        <AberturaEmpresaInfo
          plano={{
            nome: plano.nome,
            descricao: plano.descricao,
            valor: plano.valor,
          }}
        />
      )}
      <Header
        data={{
          type,
          nome: plano.nome,
          valor: plano?.valor,
          parcela: plano?.parcela ?? 1,
          valorParcela: plano?.interval ?? plano?.valorParcela,
          cupom: cupomValidado?.status === 'success' ? cupomValidado : null,
        }}
      />

      <ChooseDefaultCardShow
        userId={usuario.id}
        onFinish={handleCardChooseFinish}
      />

      {loadingCardData === false && showCardForm && (
        <>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography>Cartão de Crédito</Typography>
            <FlagCards />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Input
                control={control}
                id="holder_name"
                name="holder_name"
                label="Titular do cartão"
                error={!!errors?.holder_name}
                helperText={
                  errors?.holder_name ? errors.holder_name.message : ''
                }
                variant="outlined"
                fullWidth
                required
                disabled={hasDefaultCard}
              />
            </Grid>
            <Grid item xs={12}>
              <CardInput
                control={control}
                id="display_number"
                name="display_number"
                label="Número do cartão"
                error={!!errors?.display_number}
                helperText={
                  errors?.display_number ? errors.display_number.message : ''
                }
                variant="outlined"
                isDisplay={hasDefaultCard}
                fullWidth
                disabled={hasDefaultCard}
              />
            </Grid>
            <Grid item xs={6}>
              <InputMask
                control={control}
                id="expiration_date"
                name="expiration_date"
                label="Validade"
                pattern={DATA_VENCIMENTO_CARTAO}
                placeholder="MM/AA"
                error={!!errors?.expiration_date}
                helperText={
                  errors?.expiration_date ? errors.expiration_date.message : ''
                }
                variant="outlined"
                fullWidth
                disabled={hasDefaultCard}
              />
            </Grid>
            <Grid item xs={6}>
              <InputMask
                control={control}
                id="cvv"
                name="cvv"
                label="CVV"
                placeholder={hasDefaultCard ? 'XXX' : 'CVV'}
                pattern={CVC_PATTERN}
                error={!!errors?.cvv}
                helperText={errors?.cvv ? errors.cvv.message : ''}
                variant="outlined"
                fullWidth
                disabled={hasDefaultCard}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                name="cupom"
                label="Cupom promocional"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                error={cupomValidado?.status === 'warning'}
                helperText={cupomValidado?.message}
                onChange={(event) => setCupom(event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <InputButton
                size="large"
                variant="contained"
                fullWidth
                disabled={isCupomSubmiting || cupom === ''}
                onClick={submitCupom}
              >
                Aplicar cupom
              </InputButton>
            </Grid>
          </Grid>
          <Centered>
            <Box marginTop={4}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                disabled={
                  formSubmiting || (!isValid && hasDefaultCard === false)
                }
                onClick={handleContratarSolicitacaoPress}
              >
                {formSubmiting ? (
                  <CircularProgress color="primary" size={25} />
                ) : (
                  'Confirmar solicitação'
                )}
              </Button>
            </Box>
          </Centered>
        </>
      )}
    </Container>
  );
}

export default PagamentoComCartao;
